import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

import LandingPage from "../LandingPage";
import Message from "../Message";
import CompletedCategories from "../CompletedCategories";
import Things from "../Things";
import Buttons from "../Buttons";
import Logo from "../Logo";
import { getSelectedLogoUrl } from "../Logo";
// import GiveUp from "../GiveUp";
import Reveal from "../Reveal";
import Terms from "../Terms";
import DynamicHead from "../DynamicHead";
import MemoryGameContainer from "../Memory/MemoryGameContainer";
import ClientSpecific from "../ClientSpecific";
import DOMPurify from "dompurify";
import { resolveBackgroundImageUrl } from "../Background/backgroundImageOptions";

const defaultMessage = "Find groups of four words that share a connection.";
const maxMessage = (
  <span>
    <div>Maximum of 4 already selected.</div>
    <div>Press submit to see if you are correct.</div>
  </span>
);

const GameContainer = () => {
  const { gameId } = useParams(); // Changed from gameName to gameId
  const [game, setGame] = useState(null); // Store the full game object
  const [gameData, setGameData] = useState(null);
  const [client, setClient] = useState(null);
  const [categoryMap, setCategoryMap] = useState({});
  const [things, setThings] = useState([]);
  const [categoryColors, setCategoryColors] = useState([]);
  // const [introMessage, setIntroMessage] = useState(null); // kept for backward compatibility
  const [reveal, setReveal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [gameStarted, setGameStarted] = useState(false);
  const [completed, setCompleted] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [cardAnimation, setCardAnimation] = useState(null);
  const [timer, setTimer] = useState(null);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    return () => clearTimeout(timer);
  }, [timer]);

  const fetchGameData = useCallback(async () => {
    try {
      setLoading(true);

      if (gameId) {
        // Fetch specific game by ID
        const response = await axios.get(`/api/games/${gameId}`);
        const gameResponse = {
          ...response.data,
          type: response.data?.type || "connections",
        };

        // If inactive, set minimal state and show branded inactive screen
        if (gameResponse.isActive === false) {
          setGame(gameResponse);
          setClient(gameResponse.client);
          setError("INACTIVE");
          return;
        }

        // Process game data
        // Set shared game state
        setGame(gameResponse); // Store full game object
        setGameData(gameResponse.gameData);
        setClient(gameResponse.client);
        setCategoryColors(gameResponse.categoryColors || []);
        // introMessage no longer stored in local state

        // Enhanced reveal object with redirect data
        setReveal({
          ...gameResponse.reveal,
          successMessage: gameResponse.successMessage,
          redirectButtonText: gameResponse.redirectButtonText,
          redirectButtonLink: gameResponse.redirectButtonLink,
          redirectButtonColor: gameResponse.redirectButtonColor,
        });

        // Convert intro message object to JSX
        const formattedIntroMessage = (
          <span className="introMessage">
            {gameResponse.bannerImage ? (
              <img
                src={gameResponse.bannerImage}
                alt="Game Banner"
                className="banner-image"
              />
            ) : null}
            <h2
              className="title"
              style={{
                backgroundColor: gameResponse.buttonColor,
                color: "white",
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(gameResponse.name),
              }}
            />
            <div className="welcome-message">
              {gameResponse.introMessage.instructions}
            </div>
            <div
              className="instructions"
              dangerouslySetInnerHTML={{
                __html: gameResponse.introMessage.howTo,
              }}
            ></div>
          </span>
        );
        setMessage(formattedIntroMessage);

        if (gameResponse.type === "memory") {
          setCategoryMap({});
          setThings([]);
          return;
        }

        const categoryMapData = Object.entries(gameResponse.gameData).reduce(
          (acc, [category, items]) => {
            items.forEach((item) => {
              acc[item] = category;
            });
            return acc;
          },
          {}
        );

        const shuffledThings = Object.values(gameResponse.gameData)
          .flat()
          .sort(() => Math.random() - 0.5);

        setCategoryMap(categoryMapData);
        setThings(shuffledThings);
      } else {
        setError("SHOW_LANDING_PAGE");
        return;
      }
    } catch (error) {
      console.error("Error fetching game data:", error);
      if (error.response?.status === 403) {
        setError("INACTIVE");
      } else {
        setError(error.response?.data?.error || "Failed to load game");
      }
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]); // Changed from gameName to gameId

  const handleStartGame = () => {
    // Use custom instructions if available, fallback to default
    const gameInstructions = game?.introMessage?.instructions || defaultMessage;
    setMessage(gameInstructions);
    setGameStarted(true);
  };

  const handleSelect = (thing) => {
    if (selected.includes(thing)) {
      setSelected([...selected].filter((t) => t !== thing));
      const gameInstructions =
        game?.introMessage?.instructions || defaultMessage;
      setMessage(gameInstructions);
    } else if (selected.length >= 4) {
      setMessage(maxMessage);
    } else {
      if (selected.length === 3) {
        setMessage("Press submit if you're feeling good about it.");
      }
      setSelected([...selected, thing]);
    }
  };

  const handleDeselect = () => {
    setSelected([]);
    const gameInstructions = game?.introMessage?.instructions || defaultMessage;
    setMessage(gameInstructions);
  };

  const allFromSameCategory = () => {
    const categories = selected.map((thing) => categoryMap[thing]);
    return [...new Set(categories)].length === 1;
  };

  const handleSubmit = () => {
    const isCorrect = allFromSameCategory();
    if (isCorrect) {
      const category = categoryMap[selected[0]];
      const newCompleted = [...completed, category];

      // Check if this completes all categories (game over)
      if (newCompleted.length >= Object.keys(gameData).length) {
        // Use custom success message only when game is fully complete
        const successMsg =
          game?.successMessage || "Congratulations, you got it!";
        setMessage(successMsg);
      } else {
        // Regular category completion message
        setMessage("Great job! Keep going!");
      }
      setCardAnimation("correct");
    } else {
      setMessage("Not quite - try again!");
      setCardAnimation("incorrect");
    }

    setTimer(
      setTimeout(() => {
        if (isCorrect) {
          const category = categoryMap[selected[0]];
          setCompleted([...completed, category]);
        }
        setCardAnimation(null);
        setSelected([]);
      }, 800)
    );
  };

  // const handleGiveUp = () => {
  //   setCompleted(Object.keys(gameData));
  // };

  if (loading) {
    return (
      <div
        className="App"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <Message message="Loading game..." />
      </div>
    );
  }

  if (error) {
    if (error === "NO_GAMES_AVAILABLE" || error === "SHOW_LANDING_PAGE") {
      return <LandingPage />;
    }
    if (error === "INACTIVE") {
      return (
        <div className="App">
          <div className="inactive-game">
            <DynamicHead client={client} />
            <Logo
              client={client}
              logoUrl={getSelectedLogoUrl(client, game?.reveal)}
            />
            <Message message={"This game is not currently active."} />
          </div>
        </div>
      );
    }
    return (
      <div className="App">
        <DynamicHead client={client} />
        <Logo
          client={client}
          logoUrl={getSelectedLogoUrl(client, game?.reveal)}
        />
        <Message message={`Error: ${error}`} />
      </div>
    );
  }

  const remainingThings = things.filter(
    (thing) => !completed.includes(categoryMap[thing])
  );

  // const giveUpButton = gameStarted && <GiveUp onClick={handleGiveUp}/>;

  // Destructure styling properties from game object
  const {
    backgroundColor,
    backgroundImage,
    // bannerImage,
    buttonColor,
    textColor,
    cardBackgroundColor,
    redirectButtonColor,
    cardTextColor,
    categoryTextColor,
  } = game || {};
  const hasGradientBackground = Boolean(game?.reveal?.makeGradient);
  const resolvedBackgroundImageUrl = resolveBackgroundImageUrl(backgroundImage);
  const resolvedBackgroundImage = resolvedBackgroundImageUrl
    ? `url(${resolvedBackgroundImageUrl})`
    : hasGradientBackground
    ? `linear-gradient(to bottom, #FFFFFF 0%, ${
        backgroundColor || "#FFFFFF"
      } 100%)`
    : undefined;

  // Create inline styles for the main container
  const containerStyle = {
    backgroundColor: backgroundColor || undefined,
    color: textColor || undefined,
    backgroundImage: resolvedBackgroundImage,
    backgroundSize: resolvedBackgroundImageUrl ? "cover" : undefined,
    backgroundPosition: resolvedBackgroundImageUrl ? "center" : undefined,
    backgroundAttachment: resolvedBackgroundImageUrl ? "fixed" : undefined,
    "--card-text-color": cardTextColor || undefined,
    "--button-color": buttonColor || undefined,
    "--button-text-color": "#ffffff",
  };

  const clientClass = (client || "").toLowerCase();
  const gameType = game?.type || "connections";

  if (gameType === "memory") {
    return (
      <div
        className={`GameContainer ${clientClass}`.trim()}
        style={containerStyle}
      >
        <DynamicHead client={client} game={game} />
        <Logo
          client={client}
          logoUrl={getSelectedLogoUrl(client, game?.reveal)}
          from="game"
        />
        <MemoryGameContainer
          client={client}
          game={game}
          textColor={textColor}
          backgroundColor={backgroundColor}
          buttonColor={buttonColor}
        />
      </div>
    );
  }

  return (
    <div
      className={`GameContainer ${clientClass}`.trim()}
      style={containerStyle}
    >
      <DynamicHead client={client} game={game} />
      <Logo
        client={client}
        logoUrl={getSelectedLogoUrl(client, game?.reveal)}
        from="game"
      />
      {showTerms ? (
        <Terms
          setShowTerms={setShowTerms}
          termsContent={game?.terms}
          textColor={textColor}
          backgroundColor={backgroundColor}
          buttonColor={buttonColor}
        />
      ) : (
        <>
          <Message message={message} textColor={textColor} />
          {completed.length >= Object.keys(gameData || {}).length && (
            <ClientSpecific client={client} />
          )}
          {completed.length > 0 && (
            <CompletedCategories
              completed={completed}
              categoryThings={gameData}
              categoryColors={categoryColors}
              categoryTextColor={categoryTextColor}
            />
          )}
          {gameStarted &&
            completed.length < Object.keys(gameData || {}).length && (
              <Things
                things={remainingThings}
                selected={selected}
                onSelect={handleSelect}
                cardAnimation={cardAnimation}
                cardBackgroundColor={cardBackgroundColor}
                textColor={textColor}
                cardTextColor={cardTextColor}
              />
            )}
          <Buttons
            gameStarted={gameStarted}
            completed={completed}
            selected={selected}
            onStartGame={handleStartGame}
            onDeselect={handleDeselect}
            onSubmit={handleSubmit}
            setShowTerms={setShowTerms}
            hasTerms={game?.terms && game.terms.trim()}
            termsType={(() => {
              const t = (game?.terms || "").trim();
              if (!t) return "none";
              return /^https?:\/\//i.test(t) ? "url" : "text";
            })()}
            buttonColor={buttonColor}
            redirectButtonColor={redirectButtonColor}
            textColor={textColor}
          />
        </>
      )}
      {completed.length >= Object.keys(gameData || {}).length &&
      reveal.redirectButtonText ? (
        <Reveal {...reveal} client={client} />
      ) : null}
    </div>
  );
};

export default GameContainer;
